import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import theme, { Theme } from 'helpers/theme';
import Icon, { IconNames } from '../../../sharedComponents/core/Icon/Icon';
import ProfileIcon from 'sharedComponents/core/ProfileIcon';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import { Logout } from 'helpers/logout';
import { useRouter } from 'found';
import {
  faBuilding,
  faRightFromBracket,
  faUserPen
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsis,
  faEllipsisH,
  faEllipsisVertical
} from '@fortawesome/free-solid-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    zIndex: 10,
    height: '100%',
    flexDirection: 'column',
    borderRight: '1px solid #EDEDED',
    background: 'white',
    position: 'relative',
    justifyContent: 'space-between'
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: theme.colors.primaryWhite
  },
  tab: {
    width: '100%',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '&:hover div': {
      color: theme.colors.primaryBlack
    }
  },
  selected: {},
  tabInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    padding: '2px 5px',
    margin: '2px 10px',
    borderRadius: 8,
    transition: 'background-color 0.15s',
    '$selected &': {
      backgroundColor: '#f0f1f3'
    },
    '$tab:hover &': {
      backgroundColor: '#f8f9fb'
    }
  },
  body: {
    padding: '40px 40px',
    backgroundColor: theme.colors.backgroundGrey,
    boxShadow: theme.shadows.body,
    flexGrow: 1
  },
  menuItemText: {
    color: '#74777f',
    fontWeight: 500,
    fontSize: 13,
    transition: 'all 0.1s',
    '$selected &': {
      fontWeight: 650,
      color: theme.colors.primaryBlack
    }
  },
  menuIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32
  },
  menuHeader: {
    fontWeight: 500,
    fontSize: 12,
    color: theme.colors.secondaryGrey,
    marginLeft: 25,
    marginBottom: 6,
    marginTop: 21
  },
  sectionDivider: {
    borderTop: '1px solid #EDEDED',
    margin: '12px 0',
    padding: '1px 0'
  },
  userInfo: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 36px',
    bottom: 0,
    borderTop: '1px solid #EDEDED',
    width: '100%',
    padding: '17px 17px',
    alignItems: 'center'
  },
  userImage: {
    width: 37,
    height: 37,
    borderRadius: 7,
    background: 'blue'
  },
  userDescription: {},
  userName: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  userJob: {
    fontSize: 12,
    fontWeight: 400,
    color: '#6a728f'
  },
  moreButton: {
    color: '#6A728F',
    fontSize: 18,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    height: 36,
    '&:hover': {
      color: theme.colors.primaryBlack,
      background: '#f7f7f8'
    }
  },
  companyLogoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 21
  },
  companyLogo: {
    maxWidth: 170,
    maxHeight: 55
  }
}));

export interface Tab {
  id: number;
  icon?: any;
  iconComponent?: any;
  iconComponentSelected?: any;
  size?: number;
  name: string;
}

type Props = {
  tabs: Array<Tab>;
  companyTabs: Array<Tab>;
  selected: Tab;
  onClick: (tab: Tab) => void;
  className?: string;
  manager?: {
    firstName: string;
    lastName: string;
    profileImageUrl: string | null;
    company: {
      logoURL: string | null;
      name: string;
    };
  };
  onEditDetails?: () => void;
  onEditCompany?: () => void;
};

function SideMenu({
  tabs,
  companyTabs,
  selected,
  onClick,
  className,
  manager,
  onEditDetails,
  onEditCompany
}: Props) {
  const cssTheme = useTheme();
  const classes = useStyles({ theme: cssTheme });

  const dispatch = useActionMenuDispatch();

  const { router } = useRouter();

  const makeTab = (tab: Tab) => (
    <a
      tabIndex={0}
      key={tab.id}
      className={classNames(
        classes.tab,
        selected && selected.id === tab.id && classes.selected
      )}
      onClick={(evt) => {
        evt.preventDefault();
        onClick(tab);
      }}
    >
      <div className={classes.tabInner}>
        <div className={classes.menuIcon}>
          {(tab.iconComponent || tab.iconComponentSelected) && (
            <div
              style={{
                color:
                  selected && selected.id === tab.id
                    ? theme?.colors.primaryBlack
                    : '#74777f',
                fontSize: 13
              }}
            >
              {selected && selected.id === tab.id && tab.iconComponentSelected
                ? tab.iconComponentSelected
                : tab.iconComponent}
            </div>
          )}
          {tab.icon && !tab.iconComponent && (
            <tab.icon color={theme?.colors?.primaryGrey} />
          )}
        </div>
        <div className={classes.menuItemText}>{tab.name}</div>
      </div>
    </a>
  );

  return (
    <div className={classNames(classes.root, className)}>
      <nav>
        <div className={classes.menuHeader}>Delegates & Courses</div>
        <div className={classNames(classes.menu)}>
          {tabs && tabs.map((tab) => makeTab(tab))}
        </div>
        {companyTabs && companyTabs.length > 0 && (
          <div className={classes.sectionDivider}>
            <div className={classes.menuHeader}>Compliance</div>
            <div className={classNames(classes.menu)}>
              {companyTabs && companyTabs.map((tab) => makeTab(tab))}
            </div>
          </div>
        )}
      </nav>

      <div>
        <div className={classes.companyLogoContainer}>
          {manager?.company?.logoURL && (
            <img
              src={manager?.company?.logoURL}
              className={classes.companyLogo}
            />
          )}
        </div>
        <div className={classes.userInfo}>
          <ProfileIcon
            url={manager?.profileImageUrl ?? undefined}
            name={`${manager?.firstName} ${manager?.lastName}`}
            size={35}
          />
          <div className={classes.userDescription}>
            <div className={classes.userName}>
              {manager?.firstName} {manager?.lastName}
            </div>
            <div className={classes.userJob}>{manager?.company.name}</div>
          </div>
          <div
            onClick={(evt) => {
              dispatch({
                type: 'show',
                options: [
                  {
                    name: 'Your details',
                    onClick: onEditDetails,
                    icon: faUserPen
                  },
                  {
                    name: 'Company details',
                    onClick: onEditCompany,
                    icon: faBuilding
                  },
                  {
                    name: 'Log out',
                    onClick: () => {
                      Logout(
                        () => {},
                        () => {}
                      );
                      window.location.href = '/login';
                    },
                    icon: faRightFromBracket
                  }
                ],
                position: {
                  x: evt.clientX,
                  y: evt.clientY,
                  placement: 'top-right'
                }
              });
            }}
            className={classes.moreButton}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SideMenu;
